import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';

import { Store } from '@ngrx/store';
import { catchError, Observable, tap, throwError } from 'rxjs';

import { ConfigService, MonitoringLogger } from '@fcom/core';
import { GtmService } from '@fcom/common/gtm/services/gtm.service';
import { ElementActions, ElementTypes, GaContext } from '@fcom/common';
import { LanguageService } from '@fcom/ui-translate/services/language.service';

import { RefundsState, RefundsStatus } from '../interfaces/store.interface';
import { setRefunds } from '../store/actions/refunds.actions';
import { RefundCase } from '../interfaces/refunds.interface';

@Injectable()
export class RefundsService {
  private configService = inject(ConfigService);
  private http = inject(HttpClient);
  private monitoringLogger = inject(MonitoringLogger);
  private gtmService = inject(GtmService);
  private store$ = inject<Store<RefundsState>>(Store);
  private languageService = inject(LanguageService);

  fetchRefunds(recLoc: string, lastName: string): Observable<RefundCase[]> {
    const params: HttpParams = new HttpParams()
      .set('recLoc', recLoc)
      .set('lastName', lastName)
      .set('locale', this.languageService.localeValue);
    this.store$.dispatch(setRefunds({ refunds: [], status: RefundsStatus.LOADING }));
    const { apiUrl, keyId } = this.configService.cfg.serviceForm.customerRefunds;
    return this.http
      .get(`${apiUrl}/refunds`, {
        headers: { 'x-api-key': keyId },
        params,
      })
      .pipe(
        tap((data: RefundCase[]) => {
          this.reportFetchResult('success');
          this.store$.dispatch(setRefunds({ refunds: data, status: RefundsStatus.OK }));
        }),
        catchError((error: unknown) => {
          const err = error instanceof HttpErrorResponse ? error : ({ status: 503 } as HttpErrorResponse);
          this.reportFetchResult('error', err.status);
          this.monitoringLogger.error(`Failed to fetch refunds with status ${err.status}`, err);
          this.store$.dispatch(setRefunds({ refunds: [], status: RefundsStatus.ERROR }));
          return throwError(() => error);
        })
      );
  }

  reportFetchResult(result: 'success' | 'error', status = 200): void {
    this.gtmService.trackElement(
      `refunds-checker-form-${result}-${status}`,
      GaContext.REFUNDS,
      ElementTypes.FORM,
      undefined,
      ElementActions.SUBMIT
    );
  }
}
